import _ from 'lodash'
import axios from 'axios'
import moment, { Moment } from 'moment'
import { ActionTree } from 'vuex'
import { RootState } from '@/store/types'
import {
  Information,
  Announce,
  LessonSchedule,
  MypageState,
  TutorLessonScheduleConditions,
} from '@/store/mypage/types'
import { Deadline, DeadlineType, DeadlineWaitingApproved } from '@/store/deadlines/types'
import { Application } from '@/store/applications/types'

const actions: ActionTree<MypageState, RootState> = {
  /**
   * チューターに紐づくレッスンスケジュールを取得
   */
  fetchTutorLessonSchedule: (
    { commit, dispatch, getters },
    { tutorId, conditions }: { tutorId: string, conditions: TutorLessonScheduleConditions },
  ) => axios.get(`/tutors/${tutorId}/lessons`, {
    params: {
      ..._.pickBy({
        month: conditions?.month?.format('YYYYMM'),
        week: conditions?.week?.format('YYYYMMDD'),
        universityCode: conditions?.university?.code,
      }),
    },
  }).then(async ({ data }) => {
    await Promise.all([
      dispatch('fetchMasterUniversities'),
      dispatch('fetchMasterCampuses'),
      dispatch('fetchMasterLevels'),
      dispatch('fetchBooths', {
        conditions: _.pickBy({
          university: conditions?.university,
          removedPagingLimit: true,
        }),
      }),
    ])
    const lessonSchedules = _.map(data, (schedule: any): LessonSchedule => ({
      scheduleId: schedule.scheduleId,
      date: moment(schedule.date, 'YYYYMMDD'),
      type: schedule.type,
      unit: schedule.unit,
      level: getters.findLevelByCode(schedule.levelCode),
      course: schedule.courseCode && {
        code: schedule.courseCode,
        name: schedule.courseName,
      },
      period: schedule.period,
      university: getters.findUniversityByCode(schedule.universityCode),
      campus: getters.findCampusByCode(schedule.campusCode),
      booth: getters.findBoothById(schedule.boothId),
    }))

    commit('fetchedTutorLessonSchedules', { lessonSchedules })
  }),

  fetchMypageLessons: ({ commit }, date: Moment) => {
    const formatDate = moment(date).format('YYYYMMDD');
    return axios.get('/mypage/lessons', { params: { date: formatDate } })
      .then(({ data }) => {
        commit('fetchedMypageLessons', { data })
      })
  },

  /**
   * APIのレスポンスからチューター向け送信メール一覧の作成
   * @param {string} tutorId ログインしているチューターのID
   */
  fetchInformations: ({ commit }, tutorId?: string|undefined) => {
    return axios.get('/mails', {
      params: {
        targetType: 'tutor',
        status: 'sent',
        limit: 50,
        ..._.pickBy({
          tutorId,
        }),
      },
    })
      .then((response) => {
        const Informations: Information[] = _.map(response.data, (i: any): Information => ({
          notificationId: i.notificationId,
          sendDate: i.sendDate,
          subject: i.subject,
          sendStatusName: i.sendStatusName,
        }))
        commit('fetchedInformations', Informations)
      })
  },

  /**
   * APIのレスポンスから学生向け送信メール一覧の作成
   * @param commit
   */
  fetchAnnounces: ({ commit }) => {
    return axios.get('/mails', {
      params: {
        targetType: 'student',
        status: 'sent',
        limit: 50,
      },
    })
      .then((response) => {
        const Announces: Announce[] = _.map(response.data, (a: any): Announce => ({
          notificationId: a.notificationId,
          sendDate: a.sendDate,
          subject: a.subject,
          sendStatusName: a.sendStatusName,
        }))
        commit('fetchedAnnounces', Announces)
      })
  },

  fetchInformation: (
    { commit },
    { notificationId }: { notificationId: string },
  ) => {
    axios.get(`/mails/${notificationId}`)
      .then((res) => {
        commit('gotInformation', res.data)
      })
  },

  fetchToDoApplications: ({ commit }, lessonTutorId: string) => {
    return axios.get('/applications', {
      params: {
        lessonTutor: lessonTutorId,
        perPage: 50,
        status: 'unapproved',
        isWaitingApproved: true,
      },
    })
      .then((response) => {
        const toDoApplications: Application[] = _.map(response.data, (d: any): Application => ({
          id: d.applicationId,
          type: d.type,
          student: {
            id: d.studentId,
            studentNumber: d.studentNumber,
            name: d.studentName,
            nameEn: d.studentNameEn,
          },
          status: d.status,
          applicant: {
            id: d.applicantId,
            name: d.applicantName,
          },
          applicationDate: d.applicationDate,
        }))
        commit('fetchedToDoApplications', toDoApplications)
      })
  },

  fetchToDoWaitingApproved: ({ commit }) => {
    return axios.get('/applications/waitingapprove')
      .then((response) => {
        const toDoWaitingApproveds: DeadlineWaitingApproved[] = _.map(
          response.data, (d: any): DeadlineWaitingApproved => ({
            id: d.id,
            expired: d.expired,
            isLate: d.isLate,
            isSend: d.isSend,
            comment: d.comment,
            type: d.type,
            isWaitingApproved: d.isWaitingApproved,
            lesson: {
              id: d.lesson.id,
              datetime: moment(`${d.lesson.date}${d.lesson.startTime}`, 'YYYYMMDDhhmm'),
              dayofweek: d.lesson.dayOfWeek,
              period: d.lesson.period,
              level: {
                code: d.lesson.levelCode,
                name: '',
              },
              comment: d.lesson.comment,
              unit: d.lesson.unit,
              tutorName: d.lesson.tutorName,
            },
          }),
        )
        commit('fetchedToDoWaitingApproveds', toDoWaitingApproveds)
      })
  },

  /**
   * チューターのTODO一覧（Deadline）を取得
   * @param {string} tutorId 対象者のチューターID
   */
  fetchToDoDeadlines: ({ commit }, tutorId: string) => {
    return axios.get('/deadlines', {
      params: {
        lessonTutor: tutorId,
        deadlineSend: 'unsent',
        lessonDateTo: moment().format('YYYYMMDD'),
        type: `${DeadlineType.classRecord},${DeadlineType.frs}`,
        hasTodolist: true,
      },
    })
      .then((response) => {
        const toDoDeadlines: Deadline[] = _.map(response.data, (d: any): Deadline => ({
          id: d.id,
          expired: d.expired,
          isLate: d.isLate,
          isSend: d.isSend,
          comment: d.comment,
          type: d.type,
          isWaitingApproved: d.isWaitingApproved,
          lesson: {
            id: d.lesson.id,
            datetime: moment(`${d.lesson.date}${d.lesson.startTime}`, 'YYYYMMDDhhmm'),
            dayofweek: d.lesson.dayOfWeek,
            period: d.lesson.period,
            level: {
              code: d.lesson.levelCode,
              name: '',
            },
            comment: d.lesson.comment,
            section: {
              name: d.lesson.sectionName,
            },
            unit: d.lesson.unit,
            fiscalyear: d.lesson.fiscalYear,
          },
          tutor: {
            tutorId: d.tutor.tutorId,
            name: d.tutor.name,
          },
          students: d.students,
        }))
        commit('fetchedToDoDeadlines', toDoDeadlines)
      })
  },

  fetchEvents: ({ commit }, date: Moment) => {
    const formatDate = moment(date).format('YYYYMMDD');
    axios.get('/mypage/events', { params: { date: formatDate } })
      .then(({ data }) => {
        commit('fetchedEvents', { data })
      })
  },
}

export default actions
